import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { PasswordStrength } from '@/components'
import { useUpdatePassword } from '@/api/user-manager'
import { LoadingButton } from '@mui/lab'
import { toast } from '@/helpers'

const passwordFormSchema = z
  .object({
    password: z
      .string()
      .min(8, 'Ao menos 8 caracteres')
      .regex(new RegExp('[A-Z]', 'g'), 'Ao menos uma letra maiúscula')
      .regex(new RegExp('[0-9]', 'g'), 'Ao menos um número'),
    confirm_password: z.string().min(1, 'Obrigatório'),
  })
  .refine(({ password, confirm_password }) => password === confirm_password, {
    message: 'As senhas devem coincidir',
    path: ['confirm_password'],
  })

type PasswordFormData = z.infer<typeof passwordFormSchema>

export function PasswordForm() {
  const [showPassword, setShowPassword] = useState(false)

  const { control, handleSubmit, reset } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      password: '',
      confirm_password: '',
    },
  })

  const { mutateAsync: updatePassword, isPending: updatingPassword } =
    useUpdatePassword({
      onSuccess: () => {
        toast.success('Senha atualizada com sucesso!')
        reset()
      },
    })

  function handleUpdatePassword({ password }: PasswordFormData) {
    updatePassword({ new_password: password })
  }

  const passwordVisibilityAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label="toggle password visibility"
        onClick={() => setShowPassword(!showPassword)}
        onMouseDown={(e) => e.preventDefault()}
        edge="end"
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  )

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleUpdatePassword)}
      sx={{
        '& .MuiTextField-root': {
          mb: 2,
        },
      }}
    >
      <Controller
        name="password"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            type={showPassword ? 'text' : 'password'}
            label="Senha"
            error={Boolean(error)}
            helperText={<PasswordStrength password={field.value} />}
            slotProps={{
              input: {
                endAdornment: passwordVisibilityAdornment,
              },
            }}
            {...field}
          />
        )}
      />

      <Controller
        name="confirm_password"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <TextField
            type={showPassword ? 'text' : 'password'}
            label="Confirme a sua senha"
            error={Boolean(error)}
            helperText={error?.message}
            slotProps={{
              input: {
                endAdornment: passwordVisibilityAdornment,
              },
            }}
            {...field}
          />
        )}
      />

      <LoadingButton type="submit" loading={updatingPassword}>
        Alterar senha
      </LoadingButton>
    </Box>
  )
}
